import { useState, useEffect, Fragment } from "react";
import validator from "validator";
import { useNavigate, Link } from "react-router-dom";
import {
  mailVerification,
  registerStore,
} from "../../../../../lib/webService/webService";
import { toast } from "../../../../../utilities/toast";
import { Eye, EyeSlash, CheckLg } from "react-bootstrap-icons";
import { Seo } from "../../../../../components/Seo";
import { generateNoIndexSEO } from "../../../../../lib/seo/metaGenerators";

// ─── Module-level CSS ────────────────────────────────────────────────────────
if (typeof document !== "undefined" && !document.getElementById("sr-styles")) {
  const s = document.createElement("style");
  s.id = "sr-styles";
  s.textContent = `
    .sr-input {
      background: rgba(118,118,128,0.10);
      border: 1px solid transparent;
      border-radius: 10px;
      padding: 11px 14px;
      font-size: 0.9375rem;
      color: #1c1c1e;
      width: 100%;
      outline: none;
      transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
      font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif;
      box-sizing: border-box;
    }
    .sr-input:focus {
      border-color: #007AFF;
      box-shadow: 0 0 0 3px rgba(0,122,255,0.12);
      background: rgba(118,118,128,0.07);
    }
    .sr-input::placeholder { color: rgba(60,60,67,0.33); }
    .sr-input:disabled { opacity: 0.4; cursor: not-allowed; }
    .sr-select {
      appearance: none;
      -webkit-appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%238E8E93' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 14px center;
      padding-right: 36px;
    }
    @keyframes _sr_spin { to { transform: rotate(360deg); } }
    ._sr_spin { animation: _sr_spin 0.75s linear infinite; }
    @keyframes _sr_in { from { opacity: 0; transform: translateX(14px); } to { opacity: 1; transform: none; } }
    .sr-step { animation: _sr_in 0.22s cubic-bezier(0.34,1.24,0.64,1) both; }
  `;
  document.head.appendChild(s);
}

// ─── Constants ───────────────────────────────────────────────────────────────

const STORE_CATEGORIES = ["fast food", "liquor", "grocery", "bakery", "cafe"];
const ROLES = ["owner", "manager"];
const ACCOUNT_TYPES = ["Paybill", "Till Number"];
const CITIES = ["Nairobi", "Mombasa", "Meru", "Nanyuki", "Other"];

const PASSWORD_RULES = {
  MIN_LENGTH: 6,
  MAX_LENGTH: 16,
  SPECIAL_CHARS: /[!@#$%^&*(),.?":{}|<>]/,
  UPPERCASE: /[A-Z]/,
  LOWERCASE: /[a-z]/,
  NUMBER: /\d/,
};

const FONT =
  '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif';

const STEPS = ["Store", "Manager", "Payment"];

// ─── Atoms ───────────────────────────────────────────────────────────────────

const labelStyle = {
  fontSize: "0.72rem",
  fontWeight: 600,
  color: "rgba(60,60,67,0.55)",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  display: "block",
  marginBottom: 6,
};

const FieldGroup = ({ label, required, children, style = {} }) => (
  <div style={{ marginBottom: 14, ...style }}>
    <label style={labelStyle}>
      {label}
      {required && (
        <span style={{ color: "#FF3B30", marginLeft: 2 }}>*</span>
      )}
    </label>
    {children}
  </div>
);

const SectionLabel = ({ label }) => (
  <p style={{
    fontSize: "0.68rem", fontWeight: 600,
    color: "rgba(60,60,67,0.4)", textTransform: "uppercase",
    letterSpacing: "0.08em", margin: "0 0 14px",
  }}>
    {label}
  </p>
);

const Divider = () => (
  <div style={{ height: 1, backgroundColor: "rgba(60,60,67,0.07)", margin: "6px 0 18px" }} />
);

const ErrorAlert = ({ message, onDismiss }) => (
  <div style={{
    backgroundColor: "rgba(255,59,48,0.08)", borderRadius: 10,
    padding: "11px 14px", marginBottom: 16,
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
  }}>
    <span style={{ fontSize: "0.875rem", color: "#FF3B30", flex: 1 }}>{message}</span>
    <button
      onClick={onDismiss}
      style={{
        background: "none", border: "none", color: "#FF3B30",
        cursor: "pointer", padding: 0, fontSize: "1.125rem",
        lineHeight: 1, flexShrink: 0,
      }}
    >
      ×
    </button>
  </div>
);

const RuleItem = ({ met, label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
    <div style={{
      width: 16, height: 16, borderRadius: "50%",
      backgroundColor: met ? "#34C759" : "rgba(60,60,67,0.1)",
      display: "flex", alignItems: "center", justifyContent: "center",
      transition: "background-color 0.2s ease",
      flexShrink: 0,
    }}>
      {met && <CheckLg size={9} color="#fff" />}
    </div>
    <span style={{
      fontSize: "0.8125rem",
      color: met ? "#34C759" : "rgba(60,60,67,0.5)",
      fontWeight: met ? 500 : 400,
      transition: "color 0.2s ease",
    }}>
      {label}
    </span>
  </div>
);

// ─── Stepper ──────────────────────────────────────────────────────────────────

const Stepper = ({ step }) => (
  <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 26 }}>
    {STEPS.map((label, idx) => (
      <Fragment key={label}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 0 }}>
          <div style={{
            width: 32, height: 32, borderRadius: "50%",
            backgroundColor:
              step > idx ? "#34C759"
              : step === idx ? "#007AFF"
              : "rgba(60,60,67,0.10)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: step >= idx ? "#fff" : "rgba(60,60,67,0.35)",
            fontSize: "0.875rem", fontWeight: 700,
            transition: "background-color 0.3s ease",
            flexShrink: 0,
          }}>
            {step > idx ? <CheckLg size={14} /> : idx + 1}
          </div>
          <span style={{
            fontSize: "0.66rem",
            fontWeight: step === idx ? 700 : 400,
            marginTop: 5, whiteSpace: "nowrap",
            color:
              step > idx ? "#34C759"
              : step === idx ? "#007AFF"
              : "rgba(60,60,67,0.35)",
            transition: "color 0.3s ease",
          }}>
            {label}
          </span>
        </div>
        {idx < STEPS.length - 1 && (
          <div style={{
            flex: 1, height: 2, margin: "14px 6px 0",
            backgroundColor: step > idx ? "#34C759" : "rgba(60,60,67,0.1)",
            transition: "background-color 0.3s ease",
          }} />
        )}
      </Fragment>
    ))}
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const StoreRegister = () => {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    storeName: "", storeCategory: "", storePhone: "", altPhone: "",
    storeEmail: "", storeAddress: "", city: "", mapsLink: "",
    role: "", ownerName: "", ownerPhone: "", ownerEmail: "",
    accountType: "", account: "", accountRef: "",
    password: "", confirmPassword: "",
  });
  const [error, setError]           = useState({ isErr: false, message: "" });
  const [loading, setLoading]       = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordRules, setPasswordRules] = useState({
    minLength: false, maxLength: false, hasNumber: false,
    hasSpecialChar: false, hasUpperCase: false, hasLowerCase: false,
    passwordsMatch: false,
  });
  const navigate = useNavigate();

  useEffect(() => {
    const { password, confirmPassword } = formData;
    setPasswordRules({
      minLength:      password.length >= PASSWORD_RULES.MIN_LENGTH,
      maxLength:      password.length <= PASSWORD_RULES.MAX_LENGTH,
      hasNumber:      PASSWORD_RULES.NUMBER.test(password),
      hasSpecialChar: PASSWORD_RULES.SPECIAL_CHARS.test(password),
      hasUpperCase:   PASSWORD_RULES.UPPERCASE.test(password),
      hasLowerCase:   PASSWORD_RULES.LOWERCASE.test(password),
      passwordsMatch: password === confirmPassword && password !== "",
    });
  }, [formData.password, formData.confirmPassword]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateStep = () => {
    if (step === 0) {
      if (!formData.storeName || !formData.storeCategory || !formData.storePhone || !formData.storeEmail || !formData.storeAddress || !formData.city) {
        setError({ isErr: true, message: "Please fill all required store details." });
        return false;
      }
      if (!validator.isEmail(formData.storeEmail)) {
        setError({ isErr: true, message: "Invalid store email format." });
        return false;
      }
      return true;
    }
    if (step === 1) {
      if (!formData.role || !formData.ownerName || !formData.ownerPhone || !formData.ownerEmail) {
        setError({ isErr: true, message: "Please fill all required manager/owner details." });
        return false;
      }
      if (!validator.isEmail(formData.ownerEmail)) {
        setError({ isErr: true, message: "Invalid manager/owner email format." });
        return false;
      }
      if (!formData.password || !formData.confirmPassword) {
        setError({ isErr: true, message: "Please enter and confirm your password." });
        return false;
      }
      if (!passwordRules.minLength || !passwordRules.maxLength) {
        setError({ isErr: true, message: `Password must be ${PASSWORD_RULES.MIN_LENGTH}–${PASSWORD_RULES.MAX_LENGTH} characters` });
        return false;
      }
      if (!passwordRules.hasNumber || !passwordRules.hasSpecialChar || !passwordRules.hasUpperCase || !passwordRules.hasLowerCase) {
        setError({ isErr: true, message: "Password must meet all requirements." });
        return false;
      }
      if (!passwordRules.passwordsMatch) {
        setError({ isErr: true, message: "Passwords do not match." });
        return false;
      }
      return true;
    }
    if (step === 2) {
      if (!formData.accountType || !formData.account) {
        setError({ isErr: true, message: "Please fill all payment details." });
        return false;
      }
      return true;
    }
    return false;
  };

  const handleNext = (e) => {
    e.preventDefault();
    console.log("Current step:", step);
    setError({ isErr: false, message: "" });
    if (validateStep()) setStep(step + 1);
  };

  const handleBack = (e) => {
    e.preventDefault();
    console.log("Going back to step:", step - 1);
    setError({ isErr: false, message: "" });
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Submitting registration data:", formData);
    setError({ isErr: false, message: "" });
    if (!validateStep()) return;
    setLoading(true);
    try {
      const data = await registerStore({
        admin_email:       formData.ownerEmail,
        password:          formData.password,
        store_name:        formData.storeName,
        store_phone:       formData.storePhone,
        admin_name:        formData.ownerName,
        store_category:    formData.storeCategory,
        store_alt_phone:   formData.altPhone,
        store_email:       formData.storeEmail,
        store_address:     formData.storeAddress,
        store_city:        formData.city,
        location:          formData.mapsLink,
        admin_role:        formData.role,
        account_type:      formData.accountType,
        account_number:    formData.account,
        account_reference: formData.accountRef,
        admin_phone:       formData.ownerPhone,
      });
      if (data && data.actionLink) {
        await mailVerification(formData.ownerEmail, data.actionLink);
        toast.success("Verification email sent. Please check your inbox.");
        navigate(`/auth/verify?email=${encodeURIComponent(formData.ownerEmail)}`);
      } else {
        setError({ isErr: true, message: "Registration failed. Please try again." });
      }
    } catch (err) {
      setError({ isErr: true, message: err.message || "Registration failed. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh", backgroundColor: "#F2F2F7",
      fontFamily: FONT,
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "24px 16px",
    }}>
      <Seo {...generateNoIndexSEO("Merchant Sign Up")} />
      <div style={{ width: "100%", maxWidth: 480 }}>

        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <img
            src="/imgs/logo/complete-250.png"
            alt="FineEats"
            style={{ height: 72, width: "auto" }}
          />
        </div>

        {/* Card */}
        <div style={{
          backgroundColor: "#fff", borderRadius: 18,
          border: "1px solid rgba(15,23,42,0.07)",
          padding: "28px 24px 24px",
          boxShadow: "0 2px 12px rgba(15,23,42,0.06)",
        }}>
          <h1 style={{
            fontSize: "1.375rem", fontWeight: 700, color: "#1c1c1e",
            letterSpacing: "-0.025em", textAlign: "center", marginBottom: 4,
          }}>
            Store Registration
          </h1>
          <p style={{
            fontSize: "0.875rem", color: "rgba(60,60,67,0.52)",
            textAlign: "center", marginBottom: 24,
          }}>
            Create your store account
          </p>

          <Stepper step={step} />

          {error.isErr && (
            <ErrorAlert
              message={error.message}
              onDismiss={() => setError({ isErr: false, message: "" })}
            />
          )}

          <form onSubmit={step === 2 ? handleSubmit : handleNext}>

            {/* ── Step 0: Store Info ── */}
            {step === 0 && (
              <div className="sr-step">
                <SectionLabel label="Store Information" />
                <FieldGroup label="Store Name" required>
                  <input className="sr-input" type="text" name="storeName"
                    placeholder="Enter store name" value={formData.storeName}
                    onChange={handleInputChange} required />
                </FieldGroup>
                <FieldGroup label="Store Category" required>
                  <select className="sr-input sr-select" name="storeCategory"
                    value={formData.storeCategory} onChange={handleInputChange} required>
                    <option value="">Select category</option>
                    {STORE_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </option>
                    ))}
                  </select>
                </FieldGroup>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <FieldGroup label="Store Phone" required>
                    <input className="sr-input" type="text" name="storePhone"
                      placeholder="Phone number" value={formData.storePhone}
                      onChange={handleInputChange} required />
                  </FieldGroup>
                  <FieldGroup label="Alt. Phone">
                    <input className="sr-input" type="text" name="altPhone"
                      placeholder="Optional" value={formData.altPhone}
                      onChange={handleInputChange} />
                  </FieldGroup>
                </div>
                <FieldGroup label="Email Address" required>
                  <input className="sr-input" type="email" name="storeEmail"
                    placeholder="store@example.com" value={formData.storeEmail}
                    onChange={handleInputChange} required />
                </FieldGroup>
                <FieldGroup label="Physical Address" required>
                  <input className="sr-input" type="text" name="storeAddress"
                    placeholder="Enter store address" value={formData.storeAddress}
                    onChange={handleInputChange} required />
                </FieldGroup>
                <FieldGroup label="City / Town" required>
                  <select className="sr-input sr-select" name="city"
                    value={formData.city} onChange={handleInputChange} required>
                    <option value="">Select city/town</option>
                    {CITIES.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </FieldGroup>
                <FieldGroup label="Google Maps Link">
                  <input className="sr-input" type="url" name="mapsLink"
                    placeholder="Paste Maps link (optional)" value={formData.mapsLink}
                    onChange={handleInputChange} />
                </FieldGroup>
              </div>
            )}

            {/* ── Step 1: Manager / Owner ── */}
            {step === 1 && (
              <div className="sr-step">
                <SectionLabel label="Manager / Owner" />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <FieldGroup label="Role" required>
                    <select className="sr-input sr-select" name="role"
                      value={formData.role} onChange={handleInputChange} required>
                      <option value="">Select role</option>
                      {ROLES.map(role => (
                        <option key={role} value={role}>
                          {role.charAt(0).toUpperCase() + role.slice(1)}
                        </option>
                      ))}
                    </select>
                  </FieldGroup>
                  <FieldGroup label="Full Name" required>
                    <input className="sr-input" type="text" name="ownerName"
                      placeholder="Full name" value={formData.ownerName}
                      onChange={handleInputChange} required />
                  </FieldGroup>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <FieldGroup label="Phone" required>
                    <input className="sr-input" type="text" name="ownerPhone"
                      placeholder="Phone number" value={formData.ownerPhone}
                      onChange={handleInputChange} required />
                  </FieldGroup>
                  <FieldGroup label="Email" required>
                    <input className="sr-input" type="email" name="ownerEmail"
                      placeholder="email@example.com" value={formData.ownerEmail}
                      onChange={handleInputChange} required />
                  </FieldGroup>
                </div>

                <Divider />
                <SectionLabel label="Password" />

                <FieldGroup label="Password" required>
                  <div style={{ position: "relative" }}>
                    <input
                      className="sr-input"
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      style={{ paddingRight: 44 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      style={{
                        position: "absolute", right: 12, top: "50%",
                        transform: "translateY(-50%)",
                        background: "none", border: "none", cursor: "pointer",
                        padding: 4, color: "rgba(60,60,67,0.4)", lineHeight: 1,
                      }}
                    >
                      {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </FieldGroup>
                <FieldGroup label="Confirm Password" required>
                  <input
                    className="sr-input"
                    type={showPassword ? "text" : "password"}
                    name="confirmPassword"
                    placeholder="Confirm password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    required
                  />
                </FieldGroup>

                {/* Password requirements */}
                <div style={{
                  backgroundColor: "#F2F2F7", borderRadius: 12,
                  padding: "13px 15px", marginBottom: 4,
                }}>
                  <p style={{
                    fontSize: "0.68rem", fontWeight: 600,
                    color: "rgba(60,60,67,0.42)", textTransform: "uppercase",
                    letterSpacing: "0.07em", margin: "0 0 10px",
                  }}>
                    Requirements
                  </p>
                  <RuleItem met={passwordRules.minLength}     label={`At least ${PASSWORD_RULES.MIN_LENGTH} characters`} />
                  <RuleItem met={passwordRules.maxLength}     label={`Maximum ${PASSWORD_RULES.MAX_LENGTH} characters`} />
                  <RuleItem met={passwordRules.hasNumber}     label="Contains a number" />
                  <RuleItem met={passwordRules.hasSpecialChar} label="Contains a special character" />
                  <RuleItem met={passwordRules.hasUpperCase}  label="Contains an uppercase letter" />
                  <RuleItem met={passwordRules.hasLowerCase}  label="Contains a lowercase letter" />
                  <RuleItem met={passwordRules.passwordsMatch} label="Passwords match" />
                </div>
              </div>
            )}

            {/* ── Step 2: Payment ── */}
            {step === 2 && (
              <div className="sr-step">
                <SectionLabel label="Payment Details" />
                <FieldGroup label="Account Type" required>
                  <select className="sr-input sr-select" name="accountType"
                    value={formData.accountType} onChange={handleInputChange} required>
                    <option value="">Select account type</option>
                    {ACCOUNT_TYPES.map(type => (
                      <option key={type} value={type === "Till Number" ? "TillNumber" : "PayBill"}>
                        {type}
                      </option>
                    ))}
                  </select>
                </FieldGroup>
                <FieldGroup label="Account Number" required>
                  <input className="sr-input" type="text" name="account"
                    placeholder="Account number or mobile number"
                    value={formData.account} onChange={handleInputChange} required />
                </FieldGroup>
                <FieldGroup label="Account Reference">
                  <input
                    className="sr-input"
                    type="text"
                    name="accountRef"
                    placeholder="e.g. store name"
                    value={formData.accountRef}
                    onChange={handleInputChange}
                    disabled={formData.accountType === "TillNumber"}
                    required
                  />
                </FieldGroup>
              </div>
            )}

            {/* Navigation */}
            <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
              {step > 0 && (
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={loading}
                  style={{
                    padding: "13px 22px", borderRadius: 12,
                    border: "1px solid rgba(60,60,67,0.18)",
                    background: "none",
                    fontSize: "0.9375rem", fontWeight: 500,
                    color: "rgba(60,60,67,0.7)",
                    cursor: loading ? "not-allowed" : "pointer",
                    fontFamily: FONT, flexShrink: 0,
                  }}
                >
                  Back
                </button>
              )}
              <button
                type="submit"
                disabled={loading}
                style={{
                  flex: 1, padding: "13px", borderRadius: 12, border: "none",
                  backgroundColor: loading ? "rgba(0,122,255,0.6)" : "#007AFF",
                  color: "#fff", fontSize: "0.9375rem", fontWeight: 600,
                  cursor: loading ? "not-allowed" : "pointer",
                  fontFamily: FONT,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  transition: "background-color 0.18s ease",
                }}
              >
                {loading ? (
                  <>
                    <span style={{
                      width: 14, height: 14, borderRadius: "50%",
                      border: "2px solid rgba(255,255,255,0.35)",
                      borderTopColor: "#fff", display: "inline-block",
                    }} className="_sr_spin" />
                    {step < 2 ? "Continuing…" : "Creating account…"}
                  </>
                ) : (
                  step < 2 ? "Continue" : "Create Account"
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Footer */}
        <p style={{
          textAlign: "center", marginTop: 18, marginBottom: 32,
          fontSize: "0.875rem", color: "rgba(60,60,67,0.6)",
        }}>
          Already have an account?{" "}
          <Link
            to="/login"
            style={{ color: "#007aff", textDecoration: "none", fontWeight: 500 }}
          >
            Sign in
          </Link>
        </p>

      </div>
    </div>
  );
};

export default StoreRegister;
